package com.example.borshi.web.chat

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.example.borshi.web.firebase.FirebaseService
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.border
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.bottom
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.cursor
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flex
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.fontWeight
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.left
import org.jetbrains.compose.web.css.lineHeight
import org.jetbrains.compose.web.css.maxHeight
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.opacity
import org.jetbrains.compose.web.css.overflow
import org.jetbrains.compose.web.css.overflowY
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.right
import org.jetbrains.compose.web.css.top
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun OctopusChatPopup(isOpen: Boolean, onClose: () -> Unit) {
    var messages by remember {
        mutableStateOf(
            listOf(
                ChatMessage("Hi! I'm Octopus 🐙 Ask me about seafood, plans, or orders!", false)
            )
        )
    }
    var input by remember { mutableStateOf("") }
    var isLoading by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val sessionId = remember { "web_${js("Date.now()").toString()}" }

    if (!isOpen) return

    // Overlay Background
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(Color("rgba(0,0,0,0.3)"))
            property("z-index", "998")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.FlexEnd)
            justifyContent(JustifyContent.FlexEnd)
            padding(20.px)
        }
        onClick { onClose() }
    }) {
        // Chat Window (prevent click propagation to close)
        Div({
            style {
                width(380.px)
                maxWidth(100.percent)
                height(600.px)
                maxHeight(90.vh)
                backgroundColor(Color.white)
                borderRadius(16.px)
                property("box-shadow", "0 8px 32px rgba(0,0,0,0.2)")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                overflow("hidden")
                property("@media (max-width: 480px)", "{ width: calc(100vw - 16px); border-radius: 12px; }")
            }
            onClick { it.stopPropagation() }
        }) {
            // Header
            Div({
                style {
                    backgroundColor(Color("#00BCD4"))
                    color(Color.white)
                    padding(16.px)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(10.px)
                    }
                }) {
                    Span({ style { fontSize(24.px) } }) { Text("🐙") }
                    Div {
                        Div({ style { fontWeight("bold"); fontSize(16.px) } }) { Text("Octopus") }
                        Div({ style { fontSize(12.px); opacity(0.9) } }) { Text("Online • Replies instantly") }
                    }
                }
                Button({
                    style {
                        backgroundColor(Color("transparent"))
                        color(Color.white)
                        border(0.px)
                        fontSize(24.px)
                        cursor("pointer")
                        padding(0.px)
                        width(32.px)
                        height(32.px)
                        borderRadius(50.percent)
                        property("transition", "background-color 0.2s")
                    }
                    onClick { onClose() }
                }) {
                    Text("×")
                }
            }

            // Messages Area
            Div({
                style {
                    flex(1)
                    padding(16.px)
                    overflowY("auto")
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(12.px)
                    backgroundColor(Color("#F7F9FC"))
                }
            }) {
                messages.forEach { msg ->
                    ChatBubble(msg)
                }
                if (isLoading) {
                    ChatBubble(ChatMessage("🐙 typing...", false))
                }
            }

            // Input Area
            Div({
                style {
                    padding(12.px)
                    backgroundColor(Color.white)
                    property("border-top", "1px solid #E0E0E0")
                    display(DisplayStyle.Flex)
                    gap(8.px)
                    alignItems(AlignItems.Center)
                }
            }) {
                val sendMessage = {
                    if (input.isNotBlank() && !isLoading) {
                        val userMsg = ChatMessage(input, true)
                        val currentInput = input
                        messages = messages + userMsg
                        input = ""
                        isLoading = true

                        // Build history from last 10 messages for context
                        val history = messages.takeLast(10).map { msg ->
                            Pair(if (msg.isUser) "user" else "assistant", msg.text)
                        }

                        scope.launch {
                            // Try backend first, fall back to local keyword reply
                            val reply = FirebaseService.sendOctopusMessage(
                                sessionId = sessionId,
                                message = currentInput,
                                history = history
                            ) ?: generateReply(currentInput)

                            messages = messages + ChatMessage(reply, false)
                            isLoading = false
                        }
                    }
                }

                Input(type = InputType.Text) {
                    style {
                        flex(1)
                        padding(10.px, 14.px)
                        borderRadius(20.px)
                        border(1.px, LineStyle.Solid, Color("#E0E0E0"))
                        fontSize(14.px)
                    }
                    value(input)
                    onInput { input = it.value }
                    attr("placeholder", "Type a message...")
                    // Handle Enter key
                    onKeyDown { event ->
                        if (event.key == "Enter") {
                            event.preventDefault()
                            sendMessage()
                        }
                    }
                }

                Button({
                    style {
                        backgroundColor(if (isLoading) Color("#B0BEC5") else Color("#00BCD4"))
                        color(Color.white)
                        border(0.px)
                        borderRadius(50.percent)
                        width(40.px)
                        height(40.px)
                        cursor(if (isLoading) "not-allowed" else "pointer")
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        fontSize(18.px)
                    }
                    onClick { if (!isLoading) sendMessage() }
                }) {
                    Text("➤")
                }
            }
        }
    }
}

data class ChatMessage(val text: String, val isUser: Boolean)

@Composable
fun ChatBubble(msg: ChatMessage) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(if (msg.isUser) JustifyContent.FlexEnd else JustifyContent.FlexStart)
        }
    }) {
        Div({
            style {
                maxWidth(75.percent)
                padding(10.px, 14.px)
                borderRadius(if (msg.isUser) {
                    18.px // rounded corners
                } else {
                    18.px
                })
                backgroundColor(if (msg.isUser) Color("#00BCD4") else Color.white)
                color(if (msg.isUser) Color.white else Color("#212121"))
                property("box-shadow", "0 1px 2px rgba(0,0,0,0.1)")
                fontSize(14.px)
                lineHeight("1.4")
            }
        }) {
            Text(msg.text)
        }
    }
}

fun generateReply(input: String): String {
    val lower = input.lowercase()
    return when {
        lower.contains("hello") || lower.contains("hi") || lower.contains("hey") ->
            "Hello! 👋 How can I help you today? I can tell you about our fresh seafood, nutrition plans, or help with orders!"
        lower.contains("plan") || lower.contains("nutrition") ->
            "We have amazing nutrition plans! 💪\n• Muscle Building\n• Weight Loss\n• Heart Health\n• General Wellness\nCheck the 'Plans' tab to explore!"
        lower.contains("fish") || lower.contains("seafood") ->
            "Our top picks: 🐟\n• Fresh Salmon\n• Pomfret\n• Rohu\nVisit 'Products' to see the full menu!"
        lower.contains("prawn") || lower.contains("shrimp") ->
            "Tiger Prawns are customer favorites! 🦐 Fresh, premium quality. Want to add some to your cart?"
        lower.contains("order") || lower.contains("delivery") ->
            "Orders are delivered fresh within 24 hours! 🚚 We deliver to your area with GPS tracking. Add items to cart to get started!"
        lower.contains("price") || lower.contains("cost") ->
            "Our prices are competitive! Browse products to see rates. We also offer weekly subscription plans for savings! 💰"
        lower.contains("chicken") || lower.contains("mutton") ->
            "Yes! We have premium chicken and mutton too! 🍗🥩 Check the 'Meat' categories."
        lower.contains("help") ->
            "I'm here to help! I can assist with:\n✅ Product info\n✅ Nutrition plans\n✅ Order tracking\n✅ Delivery info\nWhat do you need?"
        else ->
            "That's a great question! 🤔 I can help you with seafood products, nutrition plans, and orders. What would you like to know?"
    }
}
